import { ComponentProps, forwardRef, useState } from 'react'
import { Input as InputH } from '@headlessui/react'
import { Icons } from '@components/atoms'
import { type VariantProps, tv } from 'tailwind-variants'

export const inputVariants = tv({
  slots: {
    container: 'flex flex-col space-y-2',
    label: 'text-white text-opacity-50 text-base font-regular font-outfit',
    input:
      'text-white text-opacity-80 rounded-md bg-input-gradient w-full border-[0.1px] border-white border-opacity-5 focus-visible:outline-double placeholder:font-outfit placeholder:text-white placeholder:text-opacity-50',
  },
  variants: {
    variant: {
      secondary: {
        input: 'bg-gray-800 outline-none',
      },
    },
    sizes: {
      lg: {
        input: 'h-16 px-5 py-6',
      },
      base: {
        input: 'h-14 px-4 py-2',
      },
      sm: {
        input: 'h-11 p-[0.625rem]',
      },
      xs: {
        input: 'h-10 p-[0.5rem]',
      },
    },
    isValid: {
      true: {
        input:
          'shadow-sm shadow-success-500 border-success-500 focus-visible:outline-success-500 border-opacity-100',
      },
    },
    isInvalid: {
      true: {
        input:
          'border-danger-500  shadow-danger-500 shadow-sm focus-visible:outline-danger-500 border-opacity-100',
      },
    },
  },
})

type TInputProps = ComponentProps<'input'> &
  VariantProps<typeof inputVariants> & {
    label: string
    message?: string
    withoutLabel?: boolean
  }

export const Input = forwardRef<HTMLInputElement, TInputProps>(
  (
    {
      label,
      id,
      sizes,
      isInvalid,
      isValid,
      message,
      type,
      className,
      withoutLabel = false,
      variant,
      ...rest
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false)

    const {
      container,
      label: labelClass,
      input,
    } = inputVariants({ sizes, isInvalid, isValid, variant })

    return (
      <div className={container()}>
        {!withoutLabel && (
          <label htmlFor={id} className={labelClass()}>
            {label}
          </label>
        )}
        <div className="relative w-full">
          <InputH
            {...rest}
            type={showPassword ? 'text' : type}
            ref={ref}
            id={id}
            className={input({ className })}
          />

          {type === 'password' && (
            <button
              type="button"
              id="btn_show_password"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute bottom-0 right-2 top-0 flex w-10 items-center justify-center">
              {showPassword ? (
                <Icons.EyeClose className="text-white text-opacity-50" />
              ) : (
                <Icons.EyeOpen className="text-white text-opacity-50" />
              )}
            </button>
          )}
        </div>
        {message && (
          <small
            data-testid="helper_message_input"
            data-invalid={isInvalid}
            className="font-outfit text-xs data-[invalid]:text-danger-500">
            {message}
          </small>
        )}
      </div>
    )
  },
)
